import type { Dict } from "../core.js";
import type { integrationRoutes as vi } from "../vi/integrationRoutes.js";

export const integrationRoutes: Dict<typeof vi> = {
  routeSummary: "The app sends through the primary route. Standby routes receive traffic only when failover conditions are met.",
  validationProcess: "How routes are validated",
  configureEmail: "Configure an email provider",

  sections: "Email route settings",
  routesTab: "Routes",
  policyTab: "Policy",
  historyTab: "History",

  title: "Mail routes",
  subtitle:
    "The app sends through the PRIMARY route; a « warm » standby (passed the gate, not expired) is the automatic failover target when the primary certainly fails. Every route choice goes through a 4-step gate: account precheck → app applies the route set → the app sends ONE real test mail → the provider reports it delivered.",
  loading: "Loading routes…",
  loadFailed: "Could not read the route set.",
  empty: "The app has no route from the hub yet — it sends with its old config (env) or cannot send. Add a primary route below.",
  noRecipient: "The hub has no HQ_MAIL_VALIDATION_RECIPIENT (test mailbox) — the gate will stop at the test send.",
  versions: "Route set v{version}",
  "col.route": "Route",
  "col.state": "State",
  "col.gate": "Gate",
  "role.primary": "Primary",
  "role.standby": "Standby",
  "role.disabled": "Disabled",
  "traffic.eligible": "takes mail",
  "traffic.validation_only": "test only",
  "status.confirmed": "passed the gate",
  "status.grandfathered_primary": "not tested (pre-existing)",
  "status.failed": "gate failed",
  "status.pending": "waiting for the gate",
  warm: "warm until {at}",
  notWarm: "not « warm » — not a failover target",
  scope: "delivery scope {scope}",
  credentialVersion: "sending config v{version}",
  "gate.running": "running",
  "gate.passed": "passed",
  "gate.failed": "failed",
  "gate.target.primary": "make primary",
  "gate.target.standby": "add standby",
  "gate.target.revalidate": "revalidate",
  "gate.by": "{target} · {actor} · {at}",
  "step.precheck": "Precheck",
  "step.stage": "App applied",
  "step.app_test": "Test send",
  "step.confirm": "Delivered",
  "step.activate": "Activate",
  "reason.set_missing": "the credential set is gone",
  "reason.decrypt_failed": "the hub cannot decrypt the set",
  "reason.account_no_key": "the set has no management key",
  "reason.account_error": "the provider account cannot be read",
  "reason.account_never": "the account has not been read yet",
  "reason.account_decrypt_failed": "the hub cannot decrypt the set",
  "reason.account_shutdown": "the account is shut down",
  "reason.sending_disabled": "sending is disabled on the account",
  "reason.sandbox": "the account is in sandbox",
  "reason.quota_exhausted": "the quota is exhausted",
  "reason.from_invalid": "the sender is not a valid email address",
  "reason.settings_invalid": "the route settings have an unknown field",
  "reason.from_missing": "the route has no sender address",
  "reason.from_domain_unverified": "the sender's domain is not verified",
  "reason.webhook_not_active": "the delivery-event webhook is not receiving — connect it on the provider page",
  "reason.app_not_applied": "the app did not apply the new route set within 10 minutes",
  "reason.no_validation_recipient": "the hub has no test mailbox",
  "reason.no_delivery_evidence": "the provider did not report delivery within 5 minutes",
  "reason.delivery_bounced": "the test mail bounced",
  "reason.delivery_rejected": "the test mail was rejected",
  "reason.delivery_complained": "the test mail was marked as a complaint",
  "reason.no_request_id": "the app returned no send id",
  "reason.app_not_sent": "the app did not send",
  "reason.app_unreachable": "the app cannot be reached",
  "reason.app_unauthorized": "the app rejected the signature",
  "reason.app_not_configured": "the hub has no URL / secret for the app",
  "reason.app_not_wired": "the app has no « Test send » endpoint",
  "reason.app_store_unavailable": "the app's database is failing",
  "reason.app_invalid_response": "the app replied off-contract",
  "reason.no_plug": "this category has no gate",
  revalidate: "Revalidate",
  remove: "Remove route",
  removeTitle: "Remove route {name}",
  removeEffect: "The app stops getting this route on the next push. The primary cannot be removed — make another route primary first.",
  removing: "Removing…",
  add: "Add / change route",
  addPick: "Credential set",
  addNone: "No email set is open to this app.",
  smtpHint: "SMTP has no delivery evidence — it cannot be chosen through the gate.",
  promote: "Test & make primary",
  standby: "Test & add standby",
  promoteTitle: "Test « {name} », then make it the primary route",
  standbyTitle: "Test « {name} », then add it as a standby",
  gateEffect: "The hub prechecks the account, pushes the route to the app as « test only », the app sends ONE real mail to the test mailbox, then waits for the provider to report it delivered (≤ 5 minutes). Only a pass changes anything — a failure keeps the current route.",
  starting: "Starting…",
  policy: "Policy",
  "policy.failover": "When the primary certainly fails",
  "policy.failover.auto": "switch to a « warm » standby",
  "policy.failover.notify": "notify only",
  "policy.failover.off": "do nothing",
  "policy.cooldown": "Cooldown between automatic switches (minutes)",
  "policy.warmHours": "Standby stays « warm » for (hours)",
  "policy.save": "Save policy",
  "policy.saving": "Saving…",
  changes: "Route change log",
  "changes.empty": "No change yet.",
  "signal.quota_exhausted": "quota exhausted",
  "signal.auth_failed": "key rejected",
  "signal.account_shutdown": "account blocked from sending",
  "signal.account_probation": "account under review",
  "signal.provider_outage": "provider not responding",
  "signal.ambiguous_outcomes": "many sends with unknown outcome",
  "signal.failover": "grounds for automatic switch",
  "change.failover": "switched to standby automatically",
  "change.app_switch_accept": "accepted the app's own switch",
  "change.app_switch_revoke": "revoked the app's own switch",
  "change.grandfather": "migrated from the old binding",
  "change.stage": "gate started",
  "change.activate": "activated",
  "change.gate_failed": "gate failed",
  "change.remove": "route removed",
  "change.policy": "policy changed",
  readOnly: "You have read-only access. Changing routes requires `hq.integrations.write`.",
  confirmType: "Type the set name « {name} » to confirm:",
  cancel: "Cancel",
  failed: "The operation did not succeed.",
  audit: "The operation is recorded in the route change log.",
  manageAt: "The app's mail route is chosen on Mail · {app}",
  routeOf: "{app}: {role}",
  "reason.sending_key_decrypt_failed": "the hub cannot open the domain's sending key",
  "change.settings": "sender changed",
  "sender.label": "Sends from:",
  "sender.none": "no sender address",
  "sender.replyToShort": "replies to {to}",
  "sender.from": "Sender address",
  "sender.fromName": "Sender name (optional)",
  "sender.replyTo": "Reply-to address (optional)",
  "sender.hint": "The sender belongs to the app on this route, not to the set — one set (provider account) can be shared by many apps. The address's domain must be verified in the set's account.",
  "sender.invalid": "The sender / reply-to must be a valid email address.",
  "sender.locked": "This set already serves the app, sending from {from} — the gate keeps that address; to change it use « Change sender » on the route card.",
  "sender.gateEffect": "The route sends from {from}.",
  "sender.edit": "Change sender",
  "sender.editHint": "The route takes mail: the hub prechecks the new address first (domain verified in the account, sending key, webhook) — on failure nothing changes; on success it switches, pushes to the app, then sends a test mail from the new address.",
  "sender.save": "Save",
  "sender.saving": "Prechecking…",
};
